import React from "react";
import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

import Button from "@mui/material/Button";
import EmailIcon from "@mui/icons-material/Email";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";

import Resume from "./chen_qian.pdf";
import vampireinc_banner from "./banners/vampireinc.png";
import seance_banner from "./banners/seance.png";
import squirrel_banner from "./banners/squirrel.png";
import tnhih_banner from "./banners/tnhih.jpeg";
import skin_deep_banner from "./banners/skin_deep.jpeg";

import Divider from "@mui/material/Divider";

import useWindowDimensions from "./helpers/useWindowDimensions";

const Item = styled((props) => (
  <Card sx={{ textAlign: "left", m: 1, maxWidth: 350 }}>
    {props.image !== undefined && (
      <CardMedia
        component="img"
        alt={props.title}
        height="140"
        image={props.image}
      />
    )}

    <CardContent>
      <Typography variant="h5" component="h3" textAlign="center" sx={{ mt: 2 }}>
        {props.title}
      </Typography>
      <Typography gutterBottom variant="h5" color="grey.400" textAlign="center">
        -
      </Typography>
      <Typography variant="body" color="text.secondary">
        {props.description}
      </Typography>
    </CardContent>
    <CardActions>
      <Button
        size="small"
        component="a"
        disabled={props.link === undefined}
        href={props.link}
        target="_blank"
        rel="noreferrer"
      >
        Learn More
      </Button>
    </CardActions>
  </Card>
))(({ theme }) => ({}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ display: "flex", p: 1, flexWrap: "wrap" }}>{children}</Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

const NavTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    maxWidth: 80,
    width: "100%",
    backgroundColor: theme.palette.primary.main,
  },
}));

const NavTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    paddingBottom: 0,
    "&.Mui-selected": {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
    },
  })
);

function App({ theme }) {
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  const { width, height } = useWindowDimensions();
  // console.log(width, height);
  return (
    <Container size="md" sx={{ mx: "auto", textAlign: "center" }}>
      <Typography variant="h1" sx={{ fontFamily: "Caveat" }} gutterBottom>
        chen<b>qian</b>
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Hi I'm Chen I do software, web and game stuff
      </Typography>
      <Divider>
        <Box>
          <Button
            aria-label="email"
            component="a"
            href="mailto:cqian550@usc.edu"
            sx={{ p: 1 }}
          >
            <EmailIcon />
          </Button>
          <Button aria-label="resume" component="a" href={Resume}>
            <DescriptionIcon />
          </Button>
          <Button
            aria-label="linkedin"
            component="a"
            href="https://www.linkedin.com/in/chen-qian/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon />
          </Button>
        </Box>
      </Divider>

      <NavTabs
        value={value}
        onChange={handleChange}
        aria-label="menu tabs"
        centered
      >
        <NavTab
          label=<Typography component="h2">Web Developer</Typography>
          {...a11yProps(1)}
        />
        <NavTab
          label={<Typography component="h2">Game Designer</Typography>}
          {...a11yProps(0)}
        />
      </NavTabs>
      <TabPanel value={value} index={0}>
        <Item
          title="Brain and Music Lab"
          description="Maintained a front end web app that collects and manages survey data for Brain and Music Lab at USC"
        />
        <Item
          title="IDPH web tool"
          description="Implemented a front end web app for health data visualization and investigation in React; iterated designs with designers and clients from Illinois Department of Public Health"
        />
        <Item
          title="ACM SIGGRAPH UIUC"
          description="Implemented the entry point for ACM SIGGRAPH UIUC"
          link="https://acm-uiuc.github.io/siggraph/"
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Item
          title="That's Not How It Happened"
          description="UI designer for a hybrid of beat'em up, visual novel and point and click game that tells the same story with three different perspectives"
          link="https://www.tnhih.com/"
          image={tnhih_banner}
        />
        <Item
          title="Skin Deep"
          description="UI designer for an interactive visual novel on eating disorder"
          link="https://skindeepthesis.tumblr.com/"
          image={skin_deep_banner}
        />
        <Item
          title="Vampire Inc."
          description="Game designer and developer for the class project Vampire Inc., a click based management simulator featuring a newborn vampire collecting blood and recruiting companions to overturn the vampire company"
          image={vampireinc_banner}
        />
        <Item
          title="Seance"
          description="Designer for a tabletop card game where the four of you went into a haunted manor to calm the spirits and hoard their treasure"
          image={seance_banner}
        />
        <Item
          title="Go Squirrel"
          description="Project lead for a 2D infinite runner game, where a cute little squirrel tries to cross the street without getting hit by the traffic"
          image={squirrel_banner}
          link="https://www.youtube.com/watch?v=FCCHRdrrfnw"
        />
      </TabPanel>
    </Container>
  );
}

export default App;
